import { useEventListener } from "@vueuse/core";
import { isAxiosError } from "axios";
import { watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "../../AuthStore/useAuthStore";
import { useServerErrorHandler } from "../../Error/serverErrorHandler";
import { isImpersonating } from "../../Impersonation/impersonationState";
import { useAppMutation } from "../../Mutation/useHydraMutations";
import { useNotificationsV2 } from "../useNotificationsV2";
import { markNotificationAsReadMutation, userNotificationIdFromIri } from "./notifications.api";
import { useUserNotificationsLive } from "./useUserNotificationsLive";

export const NOTIFICATION_QUERY_PARAM = "notification";

type ReadFailure = "not_this_account" | "transient" | "permanent";

function classifyFailure(error: unknown): ReadFailure {
  if (!isAxiosError(error)) {
    return "permanent";
  }
  const status = error.response?.status;
  if (status === 403 || status === 404) {
    return "not_this_account";
  }
  // No answer at all (offline, timeout), a server error, or "try again later".
  if (status === undefined || status >= 500 || status === 408 || status === 429) {
    return "transient";
  }
  return "permanent";
}

/** What happened to one item for one user: read, not this account's, or failed (transiently once, or for good). */
type AttemptState = "in_flight" | "read" | "not_this_account" | "failed_once" | "given_up";

/**
 * Links in notification e-mails (and opened web pushes) carry the item as `?notification=<user notification IRI>`.
 * Once the signed-in user and their preferences are known, this marks that item read and then drops the parameter,
 * so a reload or a shared URL does not read it again.
 *
 * The parameter stays while the item cannot be read by whoever is signed in here: while staff impersonate someone
 * (they must not read that person's items), and when the item belongs to another account (the API answers 403 or
 * 404). Switching this tab to the right account, or ending the impersonation, changes the user and reads it then.
 * A network error or a server error (also 408 and 429) keeps the parameter too and is retried once: when the tab
 * gets focus again, the browser is back online or the user changes. Only a permanent failure (another 4xx such as
 * 401, or an error that did not come from the request) drops it. Each user reads an item once, also when
 * several watch sources change at the same time; when the read item comes back, the parameter is just dropped.
 */
export function useReadNotificationFromLink() {
  const route = useRoute();
  const router = useRouter();
  const { user, userPreferences } = useAuthStore();
  const { isV2Enabled } = useNotificationsV2();
  const { onError } = useServerErrorHandler();
  const { refreshCountUnlessLive } = useUserNotificationsLive();
  const markReadMutation = useAppMutation(markNotificationAsReadMutation());
  const attempts = new Map<string, AttemptState>();

  async function dropParameter(): Promise<void> {
    const { [NOTIFICATION_QUERY_PARAM]: _notification, ...query } = route.query;
    await router.replace({ path: route.path, query, hash: route.hash });
  }

  async function readFromLink(): Promise<void> {
    const parameter = route.query[NOTIFICATION_QUERY_PARAM];
    const userId = user.value?.id ?? null;
    const isPublicRoute = route.meta.public === true || route.meta.skipAuthChecks === true;
    // On the login page the parameter rides along in the redirect; it is handled once the user arrives.
    if (typeof parameter !== "string" || userId === null || userPreferences.value === null || isPublicRoute || isImpersonating.value) {
      return;
    }

    const notificationId = userNotificationIdFromIri(parameter);
    if (notificationId === null || !isV2Enabled.value) {
      // Nothing this app can mark read.
      await dropParameter();
      return;
    }

    const attempt = `${userId}:${notificationId}`;
    const previous = attempts.get(attempt);
    if (previous === "read") {
      await dropParameter();
      return;
    }
    if (previous !== undefined && previous !== "failed_once") {
      return;
    }
    attempts.set(attempt, "in_flight");

    try {
      await markReadMutation.mutateAsync({ notificationId, readAt: new Date().toISOString(), isV2: true });
    } catch (error) {
      const failure = classifyFailure(error);
      if (failure === "not_this_account") {
        attempts.set(attempt, "not_this_account");
        return;
      }
      if (failure === "transient" && previous === undefined) {
        // Keep the parameter and try once more later.
        attempts.set(attempt, "failed_once");
        return;
      }
      attempts.set(attempt, "given_up");
      // The screen itself still opens; the item stays unread in the inbox.
      onError({ error, hideToast: true });
      if (failure === "permanent") {
        await dropParameter();
      }
      return;
    }

    attempts.set(attempt, "read");
    void refreshCountUnlessLive();
    await dropParameter();
  }

  watch(
    [
      () => route.query[NOTIFICATION_QUERY_PARAM],
      () => user.value?.id ?? null,
      () => userPreferences.value !== null,
      () => route.meta.public === true || route.meta.skipAuthChecks === true,
      isImpersonating,
    ],
    readFromLink,
    { immediate: true },
  );

  // A transient failure is retried once when the person comes back to the tab or the connection returns.
  useEventListener(window, ["focus", "online"], () => {
    void readFromLink();
  });
}
